import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import { useEffect } from 'react';
import DeviceGroupsSelect from './DeviceGroupsSelect';

import { usePerms } from 'contexts/PermsContext';
import { selectDeviceTypeMap } from 'features/Integrations/state/selectors/integrationSelectors';
import { useSelector } from 'react-redux';
import {
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
} from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import {
  useFusionGetIntegrationsQuery,
  useGetLicenseInfoQuery,
} from 'services/integrations/integrations.api';
import { getLicensePool } from '../../../../../config/licensepools.config';
import { getDeviceLicenseInfo } from '../../../../../utils/license.utils';
import { hasDevicePermission } from '../../../utils/devicePermissionUtils';
import { renderField } from '../utils/modalDropdownUtils';

const ModalDropdowns = ({
  deviceTypes,
  formData,
  setFormData,
  setDeviceTypes,
  device,
  layoutConfig,
}) => {
  const perms = usePerms();
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const { data: deviceGroups = [] } = useGetGroupsQuery();
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const deviceTypeMap = useSelector(selectDeviceTypeMap);
  const { data: integrationDeviceTypes } = useGetAllAvailableDeviceTypesQuery();
  const { data: licenseInfo = {} } = useGetLicenseInfoQuery();

  // system_type / device_type hold the type names themselves, so no lookup is needed.
  const selectedSystemType = formData?.system_type || '';
  const selectedDeviceType = formData?.device_type || '';
  const licensePool = getLicensePool(selectedSystemType, selectedDeviceType);
  const license = getDeviceLicenseInfo(
    { system_type: selectedSystemType, device_type: selectedDeviceType },
    licenseInfo,
  );

  // Build structured device types data from context
  useEffect(() => {
    // Ensure we have the context and a deviceTypeMap to work with.
    if (!Array.isArray(deviceTypeMap) || !Array.isArray(integrations)) return;
    const grouped = {};

    // Iterate over the list of integrations (systems) the UI shows.
    // For each integration, collect all device_type ids that belong
    // to that integration's system_type from `context.deviceTypeMap`.
    (integrations || []).forEach((integration) => {
      const system = integration.system_type;

      //Get the device types for this system from deviceTypeMap
      const deviceTypesForIntegration = deviceTypeMap
        .filter((deviceType) => deviceType?.system_type === system)
        .map((deviceType) => deviceType?.device_type)
        .filter(Boolean);

      // If user lacks create/edit permission for all device types for this system
      const hasAnyCreateEdit = deviceTypesForIntegration.some((dt) =>
        hasDevicePermission(perms, system, dt, 'createEdit'),
      );
      if (!hasAnyCreateEdit) return;

      // Initialize the grouped bucket for this system if needed, then
      // transform the device_type ids into objects that include the
      // `validDevices` list (safely reading from context.deviceTypes).
      if (!grouped[system]) grouped[system] = [];
      grouped[system] = deviceTypesForIntegration.map((dt) => ({
        type: dt,
        validDevices: integrationDeviceTypes?.[system]?.[dt]?.validDevices || [],
      }));
    });

    // Convert the grouped map into the array shape the component expects:
    // [ { system_type: '...', device_types: [ { type, validDevices }, ... ] }, ... ]
    const newDeviceTypes = Object.keys(grouped).map((system) => ({
      system_type: system,
      device_types: grouped[system],
    }));

    // Update state so dropdowns and layout lookups can use the structured data.
    setDeviceTypes(newDeviceTypes);
  }, [perms, deviceTypeMap, integrations, integrationDeviceTypes]);

  // Seed the edit form from the device's own type names, unless the user already picked.
  useEffect(() => {
    if (!device || !deviceTypes.length) return;

    setFormData((prev) => {
      if (prev.system_type && prev.device_type) return prev;

      const system = deviceTypes.find((sys) => sys.system_type === device.system_type);
      const type = system?.device_types.find((t) => t.type === device.device_type);

      return {
        ...prev,
        system_type: system?.system_type ?? '',
        device_type: type?.type ?? '',
      };
    });
  }, [device, deviceTypes]);

  const systemOptions =
    deviceTypes?.map((dt) => {
      const match = integrations?.find((i) => i.system_type === dt.system_type);
      return { value: dt.system_type, label: match?.system_name || dt.system_type };
    }) || [];

  const deviceTypeOptions =
    deviceTypes
      ?.find((dt) => dt.system_type === selectedSystemType)
      ?.device_types.map((d) => ({ value: d.type, label: d.type.replaceAll('_', ' ') })) || [];

  return (
    <div>
      <div className="d-flex flex-column gap-3">
        <BaseSelect
          label="System"
          isSearchable
          // Clearing is how the operator gets back to the neutral "Select" state after a
          // wrong pick, which the old dropdown's own "Select" row used to provide.
          isClearable
          selectProps={{
            options: systemOptions,
            value: findSelectOption(systemOptions, selectedSystemType),
            placeholder: 'Select',
            isDisabled: !!device,
            onChange: (option) =>
              setFormData({
                ...formData,
                system_type: option?.value ?? '',
                device_type: '',
                selected_device: null,
                device: null,
                bulk_mode: false,
                selected_devices: undefined,
              }),
          }}
        />
        {!!selectedSystemType && (
          <BaseSelect
            label="Device Type"
            isSearchable
            isClearable
            selectProps={{
              options: deviceTypeOptions,
              value: findSelectOption(deviceTypeOptions, selectedDeviceType),
              placeholder: 'Select',
              isDisabled: !!device,
              onChange: (option) =>
                setFormData({
                  ...formData,
                  device_type: option?.value ?? '',
                  selected_device: null,
                  device: null,
                  bulk_mode: false,
                  selected_devices: undefined,
                }),
            }}
          />
        )}

        {!!selectedSystemType &&
          !!selectedDeviceType &&
          layoutConfig &&
          layoutConfig.components.map((field, index) => {
            return (
              <div key={field['data-field'] || field.label || index}>
                {renderField(formData, setFormData, field, deviceTypes, device)}
              </div>
            );
          })}

        {licensePool !== 'virtual' && (
          <div className="fw-bold fs-6">
            <p className="mb-0">Available Licenses:</p>
            <div>{`${license?.remaining} of ${license?.limit} (Licenses remaining)`}</div>
          </div>
        )}
      </div>

      {!!selectedDeviceType && (
        <DeviceGroupsSelect
          deviceTypes={deviceTypes}
          formData={formData}
          setFormData={setFormData}
          deviceGroups={deviceGroups}
        />
      )}
    </div>
  );
};

export default ModalDropdowns;
